import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, CalendarDays, CalendarRange, CalendarClock, ClipboardList,
  ClipboardCheck, Inbox, X, ArrowRight, RefreshCw, AlertTriangle, Clock,
} from "lucide-react";
import MonthlyLeadsChart from "@/pages/admin/MonthlyLeadsChart";
import SourcePerformanceCard from "@/pages/admin/SourcePerformanceCard";
import {
  activityMeta, formatActivityWhen,
  isActivityOverdue, isActivityDueToday, todayISO,
} from "./leadHelpers";

/* America/New_York date helpers — admin business timezone. */
const BUSINESS_TZ = "America/New_York";

function nyDateString(d = new Date()) {
  // YYYY-MM-DD in business TZ (Intl en-CA gives ISO-style date).
  return new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
function nyDayOfWeek(d = new Date()) {
  // 0=Sun..6=Sat in business TZ.
  const w = new Intl.DateTimeFormat("en-US", { timeZone: BUSINESS_TZ, weekday: "short" }).format(d);
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[w];
}
function nyWeekStartEnd(d = new Date()) {
  // Returns Mon-Sun week containing today (start = Mon, end = Sun) as YYYY-MM-DD.
  const today = nyDateString(d);
  const dow = nyDayOfWeek(d);
  const mondayOffset = (dow + 6) % 7; // 0 if Mon, 6 if Sun
  const base = new Date(`${today}T12:00:00`);
  const monday = new Date(base);
  monday.setDate(base.getDate() - mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: nyDateString(monday), end: nyDateString(sunday) };
}
function nyMonthOf(d = new Date()) {
  return nyDateString(d).slice(0, 7); // YYYY-MM
}
function leadCreatedNyDate(lead) {
  if (!lead?.created_at) return "";
  try { return nyDateString(new Date(lead.created_at)); } catch { return ""; }
}
function isLeadToday(lead, today) { return leadCreatedNyDate(lead) === today; }
function isLeadInWeek(lead, start, end) {
  const d = leadCreatedNyDate(lead);
  return d >= start && d <= end;
}
function isLeadInMonth(lead, month) {
  return leadCreatedNyDate(lead).startsWith(month);
}

/* Onboarding completeness — derived from lead.onboarding_info (set by admin
 * on the lead detail page). EBT is "only if applicable" so it doesn't count
 * toward "required". */
const ONBOARDING_REQUIRED = ["personal_id", "ssn", "business_tax_id", "voided_check", "personal_phone", "business_phone", "email"];
const ONBOARDING_ALL = [...ONBOARDING_REQUIRED, "ebt_license"];

function onboardingState(lead) {
  const info = lead?.onboarding_info || {};
  const requiredResolved = ONBOARDING_REQUIRED.every((k) => {
    const s = info[k]?.status;
    return s === "received" || s === "not_applicable";
  });
  const anyTouched = ONBOARDING_ALL.some((k) => info[k] && info[k].status && info[k].status !== "needed");
  if (requiredResolved && anyTouched) return "submitted";
  if (anyTouched) return "in_progress";
  return "none";
}
const isOnboardingInProgress = (l) => onboardingState(l) === "in_progress";
const isOnboardingSubmitted = (l) => onboardingState(l) === "submitted";
const isNewUnworked = (l) => l.status === "new" && !l.last_touched_at;

/* Friendly date context strings for the dashboard header. */
function formatLong(dStr) {
  // dStr is YYYY-MM-DD in NY. We render with NY timezone explicitly so the
  // resulting string is unambiguous for the admin user.
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: BUSINESS_TZ, month: "long", day: "numeric", year: "numeric",
    }).format(new Date(`${dStr}T12:00:00`));
  } catch { return dStr; }
}
function formatLongRange(startStr, endStr) {
  return `${formatLong(startStr)} – ${formatLong(endStr)}`;
}
function formatMonthLong(monthStr) {
  // monthStr is YYYY-MM in NY.
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: BUSINESS_TZ, month: "long", year: "numeric",
    }).format(new Date(`${monthStr}-01T12:00:00`));
  } catch { return monthStr; }
}
function formatLastUpdated(d = new Date()) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TZ, hour: "numeric", minute: "2-digit",
  }).format(d);
}

/* Card */
function StatCard({ label, value, icon: Icon, onClick, testId, highlight }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group relative rounded-xl border-2 bg-white px-5 py-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-16px_rgba(0,114,255,0.35)] hover:border-[var(--swipe-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--swipe-accent)]/30 ${
        highlight ? "border-[var(--swipe-accent)]/45" : "border-[var(--swipe-accent)]/15"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
            {label}
          </div>
          <div className="mt-1.5 text-[2rem] font-extrabold text-[var(--swipe-navy)] leading-none tabular-nums">
            {value}
          </div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0 group-hover:bg-[var(--swipe-accent)] group-hover:text-white transition-colors">
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--swipe-accent)] opacity-70 group-hover:opacity-100 transition-opacity">
        View list <ArrowRight className="w-3 h-3" />
      </div>
    </button>
  );
}

/* Modal — list of business names (max 15) for a stat card.
 * onViewAll() is called when admin clicks the "View all in Leads" CTA — the
 * parent (AdminDashboard) applies the filter intent + navigates. */
function StatModal({ open, onClose, title, leads, max = 15, onViewAll, viewAllLabel = "View all in Leads" }) {
  if (!open) return null;
  const shown = leads.slice(0, max);
  const remaining = Math.max(0, leads.length - shown.length);
  return (
    <div
      onClick={onClose}
      data-testid="stat-modal"
      className="fixed inset-0 z-50 bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-[0_30px_60px_-15px_rgba(6,26,58,0.45)] w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[var(--swipe-accent)]">
              {leads.length} {leads.length === 1 ? "lead" : "leads"}
            </div>
            <h3 className="mt-0.5 font-display text-[18px] font-extrabold text-[var(--swipe-navy)]">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="stat-modal-close"
            className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-2 py-2 overflow-y-auto flex-1">
          {shown.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-[14px]" data-testid="stat-modal-empty">
              No leads found.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {shown.map((l) => (
                <li key={l.id}>
                  <Link
                    to={`/admin/leads/${l.id}`}
                    onClick={onClose}
                    data-testid={`stat-modal-lead-${l.id}`}
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-[var(--swipe-accent-soft)]/50 transition-colors"
                  >
                    <span className="font-display font-semibold text-[14.5px] text-[var(--swipe-navy)] truncate pr-2">
                      {l.business_name || l.contact_name || "Unnamed lead"}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {remaining > 0 ? (
            <div className="px-4 py-2 text-[11.5px] text-slate-400 italic">
              +{remaining} more — use &quot;View all in Leads&quot; to see the full list.
            </div>
          ) : null}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => { onClose(); onViewAll?.(); }}
            data-testid="stat-modal-view-all"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[13px] font-semibold"
          >
            {viewAllLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-semibold text-slate-500 hover:text-slate-700 px-2 py-1"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* Activity inbox — pending scheduled activities across the active+non-test
 * lead set, surfaced as Overdue first then Today. Keeps the dashboard
 * compact; "View Calendar" jumps to the full Calendar tab. */
function ActivityInbox({ leads }) {
  const items = useMemo(() => {
    const out = [];
    for (const l of leads || []) {
      for (const a of (l.scheduled_activities || [])) {
        if (a.status !== "pending") continue;
        const overdue = isActivityOverdue(a);
        const today = isActivityDueToday(a);
        if (!overdue && !today) continue;
        out.push({ activity: a, lead: l, overdue });
      }
    }
    // Overdue first, then today, both sorted by date+time.
    out.sort((x, y) => {
      if (x.overdue !== y.overdue) return x.overdue ? -1 : 1;
      const xd = `${x.activity.date || ""}T${(x.activity.time || "00:00")}`;
      const yd = `${y.activity.date || ""}T${(y.activity.time || "00:00")}`;
      return xd.localeCompare(yd);
    });
    return out;
  }, [leads]);

  const shown = items.slice(0, 6);
  const overdueCount = items.filter((i) => i.overdue).length;
  const todayCount = items.length - overdueCount;

  return (
    <div className="rounded-2xl border border-[var(--swipe-accent)]/15 bg-white p-5" data-testid="activity-inbox">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center">
            <Inbox className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-[16px] font-extrabold text-[var(--swipe-navy)] leading-tight">Activity Inbox</h3>
            <div className="text-[11.5px] text-slate-500">
              {overdueCount > 0 ? (
                <span className="text-rose-600 font-bold">Overdue: {overdueCount}</span>
              ) : null}
              {overdueCount > 0 && todayCount > 0 ? <span className="mx-1 text-slate-300">·</span> : null}
              {todayCount > 0 ? <span>Today: {todayCount}</span> : null}
              {items.length === 0 ? <span>Nothing due — you&apos;re caught up.</span> : null}
            </div>
          </div>
        </div>
        <Link
          to="/admin#calendar"
          data-testid="activity-inbox-view-calendar"
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--swipe-accent)] hover:underline"
        >
          View Calendar <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {shown.length === 0 ? (
        <div className="text-[12.5px] text-slate-400 italic py-3">
          No follow-ups due or overdue right now.
        </div>
      ) : (
        <ul className="space-y-2">
          {shown.map((it) => {
            const meta = activityMeta(it.activity.type);
            return (
              <li key={`${it.lead.id}-${it.activity.id || it.activity.date}-${it.activity.time || "x"}`}>
                <Link
                  to={`/admin/leads/${it.lead.id}`}
                  data-testid={`activity-inbox-item-${it.lead.id}`}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors ${
                    it.overdue
                      ? "border-rose-200 bg-rose-50/40 hover:bg-rose-50"
                      : "border-[var(--swipe-accent)]/20 bg-[var(--swipe-accent-soft)]/30 hover:bg-[var(--swipe-accent-soft)]/60"
                  }`}
                >
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                    it.overdue ? "bg-rose-100 text-rose-700" : "bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)]"
                  }`}>
                    {it.overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[13.5px] font-bold text-[var(--swipe-navy)] truncate">
                      {it.lead.business_name || it.lead.contact_name || "Unnamed lead"}
                    </div>
                    <div className="text-[11.5px] text-slate-600 truncate">
                      {meta.label} · {formatActivityWhen(it.activity)}
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {items.length > shown.length ? (
        <div className="mt-2 text-[11px] text-slate-400 italic">
          +{items.length - shown.length} more — see full Calendar.
        </div>
      ) : null}
    </div>
  );
}

/* Dashboard tab — stats grid (7 cards), date context, monthly chart. */
export default function DashboardTab({
  leads,
  sourceFilter,
  activeMonth,
  onPickMonth,
  onRefresh,
  onViewAllInLeads,
  onPickSource,
}) {
  const today = nyDateString();
  const { start: weekStart, end: weekEnd } = nyWeekStartEnd();
  const month = nyMonthOf();
  const lastUpdated = formatLastUpdated();

  const buckets = useMemo(() => ({
    total: leads,
    month: leads.filter((l) => isLeadInMonth(l, month)),
    week: leads.filter((l) => isLeadInWeek(l, weekStart, weekEnd)),
    today: leads.filter((l) => isLeadToday(l, today)),
    in_progress: leads.filter(isOnboardingInProgress),
    submitted: leads.filter(isOnboardingSubmitted),
    unworked: leads.filter(isNewUnworked),
  }), [leads, today, weekStart, weekEnd, month]);

  const [modal, setModal] = useState(null); // {key, title, leads, viewAllIntent}
  const open = (key, title, list, viewAllIntent) =>
    setModal({ key, title, leads: list, viewAllIntent });
  const close = () => setModal(null);

  // YYYY-MM-DD for the first/last day of the current month, derived from `month`.
  // We deliberately compute lastDay via JS Date so daylight-saving boundaries
  // don't shift it; using NY noon avoids the standard tz edge cases.
  const [monthYear, monthMonth] = month.split("-").map(Number);
  const monthStart = `${month}-01`;
  const monthEnd = (() => {
    const last = new Date(monthYear, monthMonth, 0).getDate(); // day 0 of next month = last of this month
    return `${month}-${String(last).padStart(2, "0")}`;
  })();

  const CARDS_TOP = [
    {
      key: "total", label: "Total Leads", icon: Users, value: buckets.total.length,
      title: "All active leads",
      // No date or quick filter — show every active lead.
      viewAllIntent: { quickFilter: "all" },
    },
    {
      key: "month", label: "New Leads This Month", icon: CalendarRange, value: buckets.month.length,
      title: `New leads this month · ${formatMonthLong(month)}`,
      viewAllIntent: { dateFrom: monthStart, dateTo: monthEnd },
    },
    {
      key: "week", label: "New Leads This Week", icon: CalendarDays, value: buckets.week.length,
      title: `New leads this week · ${formatLongRange(weekStart, weekEnd)}`,
      viewAllIntent: { dateFrom: weekStart, dateTo: weekEnd },
    },
    {
      key: "today", label: "New Leads Today", icon: CalendarClock, value: buckets.today.length,
      title: `New leads today · ${formatLong(today)}`,
      viewAllIntent: { dateFrom: today, dateTo: today },
    },
  ];
  const CARDS_BOTTOM = [
    {
      key: "in_progress", label: "Onboarding In Progress", icon: ClipboardList, value: buckets.in_progress.length,
      title: "Onboarding in progress",
      viewAllIntent: { quickFilter: "onboarding_in_progress" },
    },
    {
      key: "submitted", label: "Onboarding Submitted", icon: ClipboardCheck, value: buckets.submitted.length,
      title: "Onboarding submitted",
      viewAllIntent: { quickFilter: "onboarding_submitted" },
    },
    {
      key: "unworked", label: "New Unworked", icon: Inbox, value: buckets.unworked.length,
      title: "New unworked leads",
      viewAllIntent: { quickFilter: "new_unworked" },
    },
  ];

  const renderCard = (c) => (
    <StatCard
      key={c.key}
      label={c.label}
      value={c.value}
      icon={c.icon}
      testId={`stat-${c.key}`}
      highlight={c.key === "unworked" && c.value > 0}
      onClick={() => open(c.key, c.title, buckets[c.key], c.viewAllIntent)}
    />
  );

  return (
    <div data-testid="dashboard-tab" className="space-y-5">
      {/* Date context strip */}
      <div
        data-testid="dashboard-context"
        className="rounded-xl border border-[var(--swipe-accent)]/15 bg-white px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap"
      >
        <div className="flex items-center gap-3 flex-wrap text-[13px] text-slate-600">
          <span className="inline-flex items-center gap-1.5 text-[var(--swipe-navy)] font-bold">
            <CalendarClock className="w-4 h-4 text-[var(--swipe-accent)]" />
            As of {formatLong(today)}
          </span>
          <span className="hidden sm:inline text-slate-300">·</span>
          <span><span className="font-extrabold text-[var(--swipe-navy)]">Today:</span> {formatLong(today)}</span>
          <span className="hidden sm:inline text-slate-300">·</span>
          <span><span className="font-extrabold text-[var(--swipe-navy)]">This Week:</span> {formatLongRange(weekStart, weekEnd)}</span>
          <span className="hidden sm:inline text-slate-300">·</span>
          <span><span className="font-extrabold text-[var(--swipe-navy)]">This Month:</span> {formatMonthLong(month)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11.5px] text-slate-500" data-testid="dashboard-last-updated">
            Last updated: {lastUpdated}
          </span>
          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              data-testid="dashboard-refresh"
              className="w-7 h-7 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-500 inline-flex items-center justify-center"
              title="Refresh now"
              aria-label="Refresh dashboard"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Top row — 4 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS_TOP.map(renderCard)}
      </div>

      {/* Second row — 3 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {CARDS_BOTTOM.map(renderCard)}
      </div>

      <ActivityInbox leads={leads} />

      <MonthlyLeadsChart
        source={sourceFilter}
        activeMonth={activeMonth}
        onPickMonth={onPickMonth}
      />

      <SourcePerformanceCard leads={leads} onPickSource={onPickSource} />

      <StatModal
        open={!!modal}
        onClose={close}
        title={modal?.title || ""}
        leads={modal?.leads || []}
        onViewAll={() => onViewAllInLeads?.(modal?.viewAllIntent || {})}
      />
    </div>
  );
}
